import React, { useEffect, useRef, useState } from 'react';
import { FaTimes, FaMusic, FaUndo } from 'react-icons/fa';

// Type declaration to fix TypeScript error
declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

export interface EqualizerBand {
  frequency: number;
  gain: number;
  label: string;
}

interface AudioEqualizerProps {
  audioTrack: MediaStreamTrack;
  onClose: () => void;
  // New props for persisting settings
  savedBands?: EqualizerBand[];
  onSaveSettings?: (bands: EqualizerBand[]) => void;
}

const AudioEqualizer: React.FC<AudioEqualizerProps> = ({ 
  audioTrack, 
  onClose,
  savedBands,
  onSaveSettings
}) => {
  // Definir las bandas del ecualizador con frecuencias comunes
  const initialBands: EqualizerBand[] = [
    { frequency: 60, gain: 0, label: 'Bass' },
    { frequency: 150, gain: 0, label: 'Low-Mid' },
    { frequency: 400, gain: 0, label: 'Mid' },
    { frequency: 1000, gain: 0, label: 'High-Mid' },
    { frequency: 2400, gain: 0, label: 'Presence' },
    { frequency: 6000, gain: 0, label: 'Treble' }
  ];

  // Use savedBands if provided, otherwise use initialBands
  const [bands, setBands] = useState<EqualizerBand[]>(savedBands || initialBands);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Referencias para manipular el audio
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const filtersRef = useRef<BiquadFilterNode[]>([]);
  const destinationStreamRef = useRef<MediaStream | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const processingStreamRef = useRef<MediaStream | null>(null);
  
  // Configuración inicial del procesamiento de audio
  useEffect(() => {
    if (!audioTrack) return;
    
    try {
      // Crear contexto de audio - Fixed TypeScript error with proper type casting
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Crear stream de entrada desde el audio track
      const inputStream = new MediaStream([audioTrack]);
      
      // Crear nodo fuente
      const sourceNode = audioContext.createMediaStreamSource(inputStream);
      sourceNodeRef.current = sourceNode;
      
      // Crear nodo de ganancia para control de volumen general
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 1.0; // Volumen normal
      gainNodeRef.current = gainNode;
      
      // Conectar fuente a ganancia
      sourceNode.connect(gainNode);
      
      // Crear filtros para cada banda
      filtersRef.current = bands.map(band => {
        const filter = audioContext.createBiquadFilter();
        filter.type = 'peaking'; // EQ paramétrico
        filter.frequency.value = band.frequency;
        filter.gain.value = band.gain;
        filter.Q.value = 1.0; // Factor Q moderado
        return filter;
      });
      
      // Conectar los filtros en cadena
      filtersRef.current.forEach((filter, index) => {
        if (index === 0) {
          // Conectar ganancia al primer filtro
          gainNode.connect(filter);
        } else {
          // Conectar filtro anterior al actual
          filtersRef.current[index - 1].connect(filter);
        }
      });
      
      // Crear un destino para el audio procesado
      const destination = audioContext.createMediaStreamDestination();
      
      // Conectar el último filtro al destino
      if (filtersRef.current.length > 0) {
        filtersRef.current[filtersRef.current.length - 1].connect(destination);
      } else {
        // Si no hay filtros, conectar ganancia directamente al destino
        gainNode.connect(destination);
      }
      
      // Guardar referencia al stream procesado
      destinationStreamRef.current = destination.stream;
      processingStreamRef.current = inputStream;
      
      // Indicar que el procesamiento está activo
      setIsProcessing(true);
      
      console.log('Ecualizador inicializado con', bands.length, 'bandas');
      
    } catch (error) {
      console.error('Error al inicializar el ecualizador de audio:', error);
    }
    
    // Limpieza al desmontar
    return () => {
      try {
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close();
        }
      } catch (error) {
        console.error('Error al cerrar el contexto de audio:', error);
      }
    };
  }, [audioTrack]);
  
  // Actualizar el valor de ganancia cuando cambian las bandas
  useEffect(() => {
    if (!filtersRef.current.length) return;
    
    bands.forEach((band, index) => {
      if (filtersRef.current[index]) {
        filtersRef.current[index].gain.value = band.gain;
      }
    });
  }, [bands]);
  
  // Función para manejar cambios en las bandas - Updated to save settings
  const handleBandChange = (index: number, value: number) => {
    const newBands = [...bands];
    newBands[index].gain = value;
    setBands(newBands);
    
    // Save settings to parent component
    if (onSaveSettings) {
      onSaveSettings(newBands);
    }
  };
  
  // Función para resetear el ecualizador - Updated to save settings
  const handleReset = () => {
    const resetBands = bands.map(band => ({ ...band, gain: 0 }));
    setBands(resetBands);
    
    // Save settings to parent component
    if (onSaveSettings) {
      onSaveSettings(resetBands);
    }
  };
  
  // Presets comunes para voz - Updated to save settings
  const applyVocalPreset = () => {
    const vocalPreset = [
      { frequency: 60, gain: -3, label: 'Bass' },       // Reducir graves bajos
      { frequency: 150, gain: -1, label: 'Low-Mid' },   // Reducir ligeramente bajos medios
      { frequency: 400, gain: 0, label: 'Mid' },        // Mantener medios
      { frequency: 1000, gain: 3, label: 'High-Mid' },  // Aumentar medios altos para claridad
      { frequency: 2400, gain: 4, label: 'Presence' },  // Aumentar presencia para inteligibilidad
      { frequency: 6000, gain: 1, label: 'Treble' }     // Ligero aumento de agudos
    ];
    setBands(vocalPreset);
    
    // Save settings to parent component
    if (onSaveSettings) {
      onSaveSettings(vocalPreset);
    }
  };
  
  const applyWarmthPreset = () => {
    const warmthPreset = [
      { frequency: 60, gain: 2, label: 'Bass' },        // Más graves para calidez
      { frequency: 150, gain: 3, label: 'Low-Mid' },    // Más bajos medios para cuerpo
      { frequency: 400, gain: 1, label: 'Mid' },        // Ligero aumento de medios
      { frequency: 1000, gain: -1, label: 'High-Mid' }, // Reducir medios altos
      { frequency: 2400, gain: 0, label: 'Presence' },  // Mantener presencia
      { frequency: 6000, gain: -2, label: 'Treble' }    // Reducir agudos para suavidad
    ];
    setBands(warmthPreset);
    
    // Save settings to parent component
    if (onSaveSettings) {
      onSaveSettings(warmthPreset);
    }
  };
  
  const applyClarityPreset = () => {
    const clarityPreset = [
      { frequency: 60, gain: -4, label: 'Bass' },       // Reducir significativamente los graves
      { frequency: 150, gain: -2, label: 'Low-Mid' },   // Reducir bajos medios
      { frequency: 400, gain: -1, label: 'Mid' },       // Reducir ligeramente medios
      { frequency: 1000, gain: 2, label: 'High-Mid' },  // Aumentar medios altos
      { frequency: 2400, gain: 5, label: 'Presence' },  // Aumentar mucho la presencia
      { frequency: 6000, gain: 3, label: 'Treble' }     // Aumentar agudos
    ];
    setBands(clarityPreset);
    
    // Save settings to parent component
    if (onSaveSettings) {
      onSaveSettings(clarityPreset);
    }
  };
  
  return (
    <div className="audio-equalizer-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="audio-equalizer-content">
        <div className="audio-equalizer-header">
          <h3><FaMusic className="inline-block mr-2" /> <span className="text-sm">EQ</span></h3>
          <button className="close-button" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        
        <div className="audio-equalizer-body">
          <div className="audio-equalizer-presets">
            <button onClick={handleReset} className="reset-btn" title="Reset All Bands">
              <FaUndo className="mr-1" /> Reset
            </button>
            <button onClick={applyVocalPreset} title="Enhance Voice Clarity">Voice</button>
            <button onClick={applyWarmthPreset} title="Add Warmth to Voice">Warm</button>
            <button onClick={applyClarityPreset} title="Crisp & Clear Sound">Crisp</button>
          </div>
          
          <div className="audio-equalizer-bands">
            {bands.map((band, index) => (
              <div key={index} className="equalizer-band">
                <div className="band-info">
                  <span className="band-label">{band.label}</span>
                  <span className="band-value">{band.gain > 0 ? `+${band.gain}` : band.gain}</span>
                </div>
                <input
                  type="range"
                  min="-12"
                  max="12"
                  step="1"
                  value={band.gain}
                  onChange={(e) => handleBandChange(index, Number(e.target.value))}
                  className="band-slider"
                />
                <span className="band-frequency">{band.frequency < 1000 ? band.frequency : `${band.frequency/1000}k`}Hz</span>
              </div>
            ))}
          </div>
          
          <div className="processing-status">
            {isProcessing ? '✓ EQ Active' : 'Initializing...'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioEqualizer;
